import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import {
  useNotifications,
  useUnreadCount,
  markAllAsRead,
  markAsRead,
  deleteNotification,
} from '../../hooks/useNotifications';
import {
  formatNotificationDate,
  getNotificationType,
  groupNotificationsByDate,
  NOTIFICATION_TYPES,
  type Notification,
} from '../../types/notifications';
import {
  Badge,
  BellIcon,
  Button,
  Card,
  CheckIcon,
  GeneralList,
  RefreshIcon,
  Select,
  TrashIcon,
  useConfirm,
} from '../../components/ui';
import { cn } from '../../lib/utils';

type ReadFilter = 'all' | 'unread' | 'read';

const TYPE_OPTIONS = [
  { value: 'task', label: 'Tasks' },
  { value: 'ticket', label: 'Tickets' },
  { value: 'reminder', label: 'Reminders' },
  { value: 'appointment', label: 'Appointments' },
  { value: 'invoice', label: 'Invoices' },
  { value: 'client', label: 'Clients' },
];

const List: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showError, showSuccess } = useNotification();
  const [typeFilter, setTypeFilter] = useState('');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [search, setSearch] = useState('');

  const { notifications, isLoading, mutate } = useNotifications({ limit: 200 });
  const { count: unreadCount, mutate: mutateUnread } = useUnreadCount();

  const readCount = useMemo(
    () => notifications.filter((n) => !!n.readAt).length,
    [notifications],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return notifications.filter((n) => {
      if (readFilter === 'unread' && n.readAt) return false;
      if (readFilter === 'read' && !n.readAt) return false;
      if (typeFilter) {
        const t = getNotificationType(n.entityType || '');
        if (t !== typeFilter) return false;
      }
      if (q && !(n.message || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [notifications, typeFilter, readFilter, search]);

  const readTabs = useMemo(
    () => [
      { id: 'all' as const, label: 'All', count: notifications.length },
      { id: 'unread' as const, label: 'Unread', count: unreadCount },
      { id: 'read' as const, label: 'Read', count: readCount },
    ],
    [notifications.length, unreadCount, readCount],
  );

  const grouped = useMemo(() => groupNotificationsByDate(filtered), [filtered]);

  const hasFilters = Boolean(search.trim() || typeFilter || readFilter !== 'all');

  const refresh = () => {
    mutate();
    mutateUnread();
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      refresh();
      showSuccess('All notifications marked as read');
    } catch (error: any) {
      showError(error?.message || 'Failed to mark all as read');
    }
  };

  const handleOpen = async (n: Notification) => {
    if (!n.readAt) {
      try {
        await markAsRead(n.id);
        refresh();
      } catch (error: any) {
        showError(error?.message || 'Failed to mark as read');
      }
    }
    if (n.link) navigate(n.link);
  };

  const handleDelete = async (e: React.MouseEvent, n: Notification) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Delete notification',
      message: 'Remove this notification from your inbox?',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteNotification(n.id);
      refresh();
    } catch (error: any) {
      showError(error?.message || 'Failed to delete notification');
    }
  };

  const renderGroup = (label: string, items: Notification[]) => {
    if (!items.length) return null;
    return (
      <section key={label} className="mb-5 last:mb-0">
        <div className="mb-2 flex items-center gap-2.5 px-1">
          <span className={cn('h-px w-3 shrink-0', colors.primary)} aria-hidden />
          <h2 className={cn('text-[11px] font-semibold uppercase tracking-[0.18em]', colors.textMuted)}>
            {label}
          </h2>
          <Badge variant="secondary" className="tabular-nums">
            {items.length}
          </Badge>
        </div>
        <Card className="overflow-hidden divide-y divide-inherit !rounded-xl p-0" variant="default">
          <div className={cn(colors.divide, 'divide-y')}>
            {items.map((n) => {
              const type = getNotificationType(n.entityType || '');
              const typeMeta = NOTIFICATION_TYPES[type];
              const isRead = !!n.readAt;
              return (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(n)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpen(n);
                    }
                  }}
                  className={cn(
                    'group flex w-full cursor-pointer items-start gap-4 px-4 py-4 text-left transition-colors',
                    colors.interactiveHover,
                    !isRead && cn(colors.surfaceHover, 'border-l-[3px]', colors.primaryBorder),
                    isRead && 'border-l-[3px] border-l-transparent',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                      colors.surfaceHover,
                      colors.primaryFg,
                    )}
                    aria-hidden
                  >
                    <BellIcon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant="secondary">{typeMeta.label}</Badge>
                      {!isRead && <Badge variant="info">Unread</Badge>}
                    </div>
                    <p className={cn('mt-1.5 text-sm leading-relaxed', colors.text, !isRead && 'font-semibold')}>
                      {n.message}
                    </p>
                    <p className={cn('mt-1.5 text-xs', colors.textMuted)}>
                      {formatNotificationDate(n.createdAt)}
                      {n.link ? ' · Open related record' : ''}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(e) => handleDelete(e, n)}
                    className="shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 hover:!text-red-600"
                    title="Delete"
                    aria-label="Delete notification"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </Card>
      </section>
    );
  };

  return (
    <GeneralList
      title="Notifications"
      description={
        unreadCount > 0
          ? `${unreadCount} unread · ${notifications.length} total`
          : notifications.length > 0
            ? `You're up to date · ${notifications.length} total`
            : 'Your notification inbox'
      }
      tabs={readTabs}
      activeTab={readFilter}
      onTabChange={setReadFilter}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search notifications…',
        title: 'Search by message text',
      }}
      actions={
        <>
          <div className="w-[160px] sm:w-[180px]">
            <Select
              value={typeFilter}
              options={TYPE_OPTIONS}
              placeholder="All types"
              showClearButton
              onChange={(e) => setTypeFilter(e.target.value)}
            />
          </div>
          {unreadCount > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={handleMarkAllAsRead}>
              <CheckIcon className="h-4 w-4" />
              Mark all as read
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshIcon className="h-4 w-4" />
            Refresh
          </Button>
        </>
      }
      hasFilters={hasFilters}
      onClearFilters={() => {
        setSearch('');
        setTypeFilter('');
        setReadFilter('all');
      }}
      resultSummary={
        <p className={cn('text-xs sm:text-sm', colors.textMuted)}>
          Showing <span className={cn('font-medium', colors.text)}>{filtered.length}</span>
          {filtered.length !== notifications.length ? ` of ${notifications.length}` : ''} notifications
        </p>
      }
      isLoading={isLoading}
      loadingLabel="Loading notifications…"
      isEmpty={!isLoading && filtered.length === 0}
      emptyState={{
        icon: <BellIcon className="h-12 w-12" />,
        title: notifications.length === 0 ? 'No notifications yet' : 'No matching notifications',
        hint:
          notifications.length === 0
            ? 'New activity will show up here.'
            : 'Try clearing filters or search.',
        action:
          hasFilters ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSearch('');
                setTypeFilter('');
                setReadFilter('all');
              }}
            >
              Clear filters
            </Button>
          ) : (
            <Button type="button" variant="outline" size="sm" onClick={() => navigate('/dashboard')}>
              Back to dashboard
            </Button>
          ),
      }}
    >
      <div className="space-y-5 p-4 sm:p-5">
        {renderGroup('Today', grouped.today)}
        {renderGroup('Yesterday', grouped.yesterday)}
        {renderGroup('This Week', grouped.thisWeek)}
        {renderGroup('Older', grouped.older)}
      </div>
    </GeneralList>
  );
};

export default List;
